import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminCalendar = () => {
  const { token } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, [currentMonth]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      const response = await fetch(
        `${API_URL}/api/admin/calendar/bookings?start=${start}&end=${end}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Error fetching calendar bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBookingsForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return bookings.filter(b => {
      const start = b.start_date?.split('T')[0] || b.start_date;
      const end = b.end_date?.split('T')[0] || b.end_date;
      return dayStr >= start && dayStr <= end;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-500';
      case 'paid': return 'bg-blue-500';
      case 'pending': return 'bg-amber-500';
      default: return 'bg-slate-400';
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Ausstehend', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      paid: { label: 'Bezahlt', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      confirmed: { label: 'Bestätigt', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    };
    const c = config[status] || config.pending;
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad start to Monday
  const startDay = monthStart.getDay();
  const paddingDays = startDay === 0 ? 6 : startDay - 1;
  const paddedDays = Array(paddingDays).fill(null).concat(days);

  const selectedDayBookings = selectedDay ? getBookingsForDay(selectedDay) : [];

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold">Buchungskalender</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Übersicht aller Buchungen im Kalender
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-2">
          <Card className="card-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} data-testid="calendar-prev-month">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <CardTitle className="text-lg">
                  {format(currentMonth, 'MMMM yyyy', { locale: de })}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} data-testid="calendar-next-month">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
                  <div key={d} className="text-center text-xs font-medium text-[hsl(var(--muted-foreground))] py-2">{d}</div>
                ))}
              </div>
              {/* Calendar cells */}
              <div className="grid grid-cols-7 gap-1">
                {paddedDays.map((day, idx) => {
                  if (!day) return <div key={`pad-${idx}`} className="h-20 rounded-md" />;
                  const dayBookings = getBookingsForDay(day);
                  const isSelected = selectedDay && isSameDay(day, selectedDay);
                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => setSelectedDay(day)}
                      className={`h-20 rounded-md border text-left p-1 transition-all hover:bg-[hsl(var(--muted)/0.5)] ${
                        isSelected ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.05)] ring-1 ring-[hsl(var(--primary))]' :
                        isToday(day) ? 'border-[hsl(var(--primary)/0.3)] bg-[hsl(var(--primary)/0.02)]' :
                        'border-[hsl(var(--border))]'
                      }`}
                      data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                    >
                      <span className={`text-xs font-medium ${
                        isToday(day) ? 'text-[hsl(var(--primary))] font-bold' : 'text-[hsl(var(--foreground))]'
                      }`}>
                        {format(day, 'd')}
                      </span>
                      <div className="mt-1 space-y-0.5">
                        {dayBookings.slice(0, 2).map((b, i) => (
                          <div key={b.id + i} className={`${getStatusColor(b.status)} h-1 rounded-full`} />
                        ))}
                        {dayBookings.length > 2 && (
                          <span className="text-[9px] text-[hsl(var(--muted-foreground))]">+{dayBookings.length - 2}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[hsl(var(--border))]">
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-xs">Bestätigt</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-blue-500" /><span className="text-xs">Bezahlt</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-amber-500" /><span className="text-xs">Ausstehend</span></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Selected Day Detail */}
        <div>
          <Card className="card-shadow sticky top-4">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {selectedDay ? format(selectedDay, 'EEEE, d. MMMM yyyy', { locale: de }) : 'Tag auswählen'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedDay ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-8">
                  Klicken Sie auf einen Tag, um die Buchungen anzuzeigen.
                </p>
              ) : selectedDayBookings.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-8">
                  Keine Buchungen an diesem Tag.
                </p>
              ) : (
                <div className="space-y-3" data-testid="calendar-day-bookings">
                  {selectedDayBookings.map(b => (
                    <motion.div
                      key={b.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)]"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                          <span className="font-medium text-sm">{b.location?.name || '-'}</span>
                        </div>
                        {getStatusBadge(b.status)}
                      </div>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{b.customer_name}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">{b.start_date?.split('T')[0]} - {b.end_date?.split('T')[0]}</p>
                      <p className="text-xs font-semibold mt-1">CHF {Number(b.total_price).toFixed(2)}</p>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminCalendar;
